import React, { useEffect, useRef, useState, forwardRef } from "react"
import { Text, FlexContainer, Container } from "@carrotfertility/carotene"
import { Box, Checkbox, Divider, FormControlLabel, Typography, useTheme } from "@carrotfertility/carotene-core"
import styled, { ThemeProvider } from "styled-components"
import { SinglePathSVG } from "../views/SVG"
import { CarrotPlan } from "../../types/carrotPlanTypes"
// @ts-ignore
import colors from "css/variables/_colors.scss"
import InfoOutlineIcon from "../views/atoms/images/InfoOutlineIcon"
import CarrotPlansTourModal from "./CarrotPlansTourModal"
import { useToggle } from "utils/Hooks"
import { Checkbox as LegacyCheckbox } from "../views/atoms/Checkbox"
import { carrotPlanHeroTheme } from "../../themes/formThemes"
import useCarrotPlanMenuItems, { CarrotPlanStepMenuItem } from "../carrot-plans/hooks/useCarrotPlanMenuItems"
import { Error } from "../views/Error"
import { reportErrorMessage } from "../../utils/ErrorReporting"
import { useQueryClient } from "@tanstack/react-query"
import { getCarrotPlanQueryKey } from "components/carrot-plans/hooks/useCarrotPlan"
import Tooltip from "../Tooltip"
import useUpdatePlanStep from "../carrot-plans/hooks/useUpdatePlanStep"
import { FormattedMessage, useIntl } from "react-intl"
import moment from "moment"

type CarrotPlanHeroProps = {
  plan: CarrotPlan
}

type PlanStepProps = {
  carrotPlanStepMenuItem: CarrotPlanStepMenuItem
  employeeId: string | number
}

interface StepContentProps extends Omit<PlanStepProps, "employeeId">, React.HTMLAttributes<HTMLSpanElement> {
  stepCompleted: boolean
}

type StepCheckmarkProps = {
  carrotPlanStepMenuItem: CarrotPlanStepMenuItem
  stepCompleted: boolean
  handleStepCompleted: (stepId: number) => void
}

// This file does too much. We should look to split this into multiple files when given the opportunity

const TourButton = styled.button`
  cursor: pointer;
  padding: 0;
  background: none;
  border: none;
  :hover {
    opacity: 0.5;
    transition: opacity 0.15s ease-in;
  }
`

const TourIcon = styled(InfoOutlineIcon)`
  margin-bottom: -2px;
`

export default function CarrotPlanHero({ plan }: CarrotPlanHeroProps): JSX.Element {
  const [showTourModal, toggleShowTourModal] = useToggle(false)
  const intl = useIntl()
  const { locale } = intl
  const theme = useTheme()

  const {
    data: carrotPlanStepMenuItems,
    isLoading: menuItemsLoading,
    error: menuItemsError
  } = useCarrotPlanMenuItems(plan.steps, locale)

  if (menuItemsError) {
    reportErrorMessage(
      `Unable to retrieve Carrot Plan menu items from Contentful for employee ${plan.employeeId}, error: ${menuItemsError}`
    )
    return <Error />
  }
  const createdAtUtcDate: Date = moment(plan.createdAtUtc).toDate()

  return (
    <>
      {!menuItemsLoading ? (
        <Box
          id="carrot-plans-hero"
          width="100%"
          display="flex"
          component="article"
          gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
          flexDirection="column"
          borderRadius={(theme) => theme.tokens.borderRadius.md}
          bgcolor="#15415b"
          color={(theme) => theme.palette.secondary.contrastText}
          padding={(theme) => theme.spacing(theme.tokens.spacing.lg)}
        >
          <Box alignItems="center" display="flex" flexDirection="row" justifyContent="space-between">
            <Box display="flex" gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}>
              <Typography variant="h3" component="h2" color="inherit">
                <FormattedMessage defaultMessage="Your Carrot Plan" />
              </Typography>
              <TourButton
                aria-haspopup="dialog"
                style={{ marginLeft: theme.spacing(theme.tokens.spacing.xs) }}
                aria-label={intl.formatMessage({ defaultMessage: "How to use Carrot Plans" })}
                onClick={toggleShowTourModal}
              >
                <TourIcon color="white" opacity="0.8" />
              </TourButton>
            </Box>
            <CarrotPlansTourModal open={showTourModal} onClose={toggleShowTourModal} />

            <Typography variant="body2" color="inherit" data-testid="carrot-plans-hero">
              <FormattedMessage
                defaultMessage="Updated {formattedDate}"
                values={{
                  formattedDate: intl.formatDate(createdAtUtcDate, {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })
                }}
              />
            </Typography>
          </Box>
          <Box
            component="ul"
            display="flex"
            gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
            flexDirection="column"
            sx={{ "li::before": { display: "none" } }}
          >
            {carrotPlanStepMenuItems.map((stepMenuItem) => (
              <Box
                component="li"
                display="flex"
                gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
                flexDirection="column"
                key={stepMenuItem?.carrotPlanStepId}
              >
                <Divider role="presentation" />
                <PlanStep employeeId={plan?.employeeId} carrotPlanStepMenuItem={stepMenuItem} />
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </>
  )
}

type StepCheckboxAndTextContainerProps = { markedNotRelevant: boolean }
const StepCheckboxAndTextContainer = styled.div<StepCheckboxAndTextContainerProps>`
  display: flex;
  margin-right: min(16px);
  opacity: ${(props) => props.markedNotRelevant && ".5"};
`

const StepLink = styled.a`
  color: #ffffff;
  text-decoration-line: none;
  padding-right: 17px;
  margin-left: 16px;
  align-items: center;
  display: flex;
  cursor: pointer;
  line-height: 23px;
  > svg {
    margin-left: 9px;
    display: block;
    vertical-align: top;
    margin-top: 4px;
  }
`

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const MenuButton = styled.button`
  cursor: pointer;
  align-self: flex-end;
  background: none;
  border: none;
  padding: 0;
  color: white;
  :hover {
    opacity: 0.5;
    transition: opacity 0.15s ease-in;
  }
`

type LockedStepCheckboxProps = {
  checked: boolean
} & React.HTMLAttributes<HTMLDivElement>

const LockedStepHiddenCheckbox = styled.input.attrs({ type: "checkbox", readOnly: true })`
  // Hide checkbox visually but remain accessible to screen readers.
  // Source: https://polished.js.org/docs/#hidevisually
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`
const LockedStepCheckboxIcon = styled.svg`
  fill: none;
  stroke: ${(props) => props.theme.checkboxCheckColor};
  stroke-width: 2px;
`

const LockedStepStyledCheckbox = styled.div<LockedStepCheckboxProps>`
  display: inline-block;
  width: 24px;
  height: 24px;
  background: ${(props) =>
    props.checked ? props.theme.checkedCheckboxBackgroundColor : props.theme.checkboxBackgroundColor || "#ffffff"};
  border-radius: 3px;
  border-color: ${(props) => props.theme.checkboxBorderColor || "#e48400"};
  border-width: 2px;
  border-style: solid;
  ${LockedStepCheckboxIcon} {
    visibility: ${(props) => (props.checked ? "visible" : "hidden")};
  }
`

const LockedStepVisibleCheckbox = ({ checked }: LockedStepCheckboxProps): JSX.Element => (
  <LockedStepStyledCheckbox checked={checked}>
    <LockedStepCheckboxIcon viewBox="0 0 24 24">
      <polyline points="20 6 9 17 4 12" />
    </LockedStepCheckboxIcon>
  </LockedStepStyledCheckbox>
)

const LockedStepCheckboxContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
`

// eslint-disable-next-line react/display-name
const LockedStepCheckbox = forwardRef(
  ({ checked, ...otherProps }: LockedStepCheckboxProps, ref: React.RefObject<never>): JSX.Element => {
    return (
      <LockedStepCheckboxContainer ref={ref}>
        <LockedStepHiddenCheckbox aria-disabled="true" checked={checked} {...otherProps} />
        <LockedStepVisibleCheckbox checked={checked} />
      </LockedStepCheckboxContainer>
    )
  }
)

const LockedStepCheckboxTooltipContent = ({ checked, ...otherProps }: LockedStepCheckboxProps): JSX.Element => {
  let tooltipText = "We'll check this one for you when it's done"
  if (checked) {
    tooltipText = "We checked this one for you"
  }
  return (
    <Container padding="small" {...otherProps}>
      <Text size="small">{tooltipText}</Text>
    </Container>
  )
}

export function getStepMenuItemDisplayText(
  carrotPlanStepMenuItem: CarrotPlanStepMenuItem,
  locale: string
): JSX.Element | string {
  const displayText = carrotPlanStepMenuItem?.displayText

  if (carrotPlanStepMenuItem?.readLearnArticleText) {
    let readLearnArticleText = carrotPlanStepMenuItem?.readLearnArticleText

    switch (locale) {
      case "fr-CA":
        return displayText.replace(/«(.*?)»/g, "« " + readLearnArticleText + " »")
      case "es-US":
        return displayText?.replace(/"\[(.+?)]"/g, "“" + readLearnArticleText + "”")
      default:
        // Note: Once WQ-1509 is completed and the "getCpmenuitemFromContentful" feature flag is no longer in use
        // this startsWith("all ") case can be removed
        if (!readLearnArticleText.startsWith("all ")) {
          readLearnArticleText = "“" + readLearnArticleText + "”"
        }
        return displayText?.replace(/"\[(.+?)]"/g, readLearnArticleText)
    }
  } else if (carrotPlanStepMenuItem?.entryId && !carrotPlanStepMenuItem?.readLearnArticleText) {
    return (
      <FormattedMessage defaultMessage="Check out our Read & learn resources for more information about your journey" />
    )
  }
  return displayText
}

function StepContent({ carrotPlanStepMenuItem: step, stepCompleted, ...otherProps }: StepContentProps): JSX.Element {
  const intl = useIntl()
  const { locale } = intl
  const showLink = React.useMemo(
    () => Boolean(step?.url) && !(step?.isLocked && stepCompleted),
    [step?.isLocked, step?.url, stepCompleted]
  )

  // @ts-expect-error TS7006
  const [onClick, href, target] = React.useMemo(() => [(e) => e.currentTarget.blur(), step?.url, "_blank"], [step?.url])

  if (showLink) {
    return (
      <StepLink {...{ onClick, href, target }}>
        <span {...otherProps}>{getStepMenuItemDisplayText(step, locale)}</span>
        <SinglePathSVG fill={colors.carrotOrange} icon="rightChevron" inline={true} />
      </StepLink>
    )
  }
  return (
    <FlexContainer paddingLeftRight="small">
      <Text {...otherProps} variant="light">
        {step?.displayText}
      </Text>
    </FlexContainer>
  )
}

function StepCheckmark({
  carrotPlanStepMenuItem: step,
  stepCompleted,
  handleStepCompleted,
  ...otherProps
}: StepCheckmarkProps): JSX.Element {
  return (
    <label>
      <ThemeProvider theme={carrotPlanHeroTheme}>
        {step?.isLocked ? (
          <Tooltip
            trigger="mouseenter focusin"
            placement="bottom-start"
            offset={[-20, 16]}
            content={(): JSX.Element => <LockedStepCheckboxTooltipContent role="alert" checked={stepCompleted} />}
          >
            <LockedStepCheckbox {...otherProps} checked={stepCompleted} />
          </Tooltip>
        ) : (
          <LegacyCheckbox
            {...otherProps}
            size="24px"
            checked={stepCompleted}
            onChange={() => handleStepCompleted(step?.carrotPlanStepId)}
          />
        )}
      </ThemeProvider>
    </label>
  )
}

const PlanStep = ({ carrotPlanStepMenuItem: step, employeeId }: PlanStepProps): JSX.Element => {
  const [stepCompleted, setStepCompleted] = useState(!!step?.completedAtUtc)
  const query = useQueryClient()
  const [markedNotRelevant, setMarkedNotRelevant] = useState(!!step?.markedNotRelevantAtUtc)
  const [showNotRelevant, toggleShowNotRelevant] = useToggle(false)
  const { mutate } = useUpdatePlanStep()
  const intl = useIntl()
  const popoverWrapperRef = useRef(null)
  useOutsideAlerter(popoverWrapperRef, () => toggleShowNotRelevant(false))

  const handleStepCompleted = React.useCallback(
    (stepId: number): void => {
      setStepCompleted(!stepCompleted)
      mutate(
        { stepId, completed: !stepCompleted },
        { onSuccess: () => query.invalidateQueries(getCarrotPlanQueryKey()) }
      )
    },
    [mutate, query, stepCompleted]
  )

  const handleNotRelevant = React.useCallback(
    (stepId: number): void => {
      setMarkedNotRelevant(!markedNotRelevant)
      mutate(
        { stepId, markedNotRelevant: !markedNotRelevant },
        { onSuccess: () => query.invalidateQueries(getCarrotPlanQueryKey()) }
      )
    },
    [markedNotRelevant, mutate, query]
  )

  return (
    <Box display="flex" justifyContent="space-between">
      <StepCheckboxAndTextContainer markedNotRelevant={markedNotRelevant}>
        <StepCheckmark
          aria-labelledby={`cp-item-${step?.carrotPlanStepId}-label`}
          {...{ carrotPlanStepMenuItem: step, stepCompleted, handleStepCompleted }}
        />
        <StepContent
          id={`cp-item-${step?.carrotPlanStepId}-label`}
          {...{ carrotPlanStepMenuItem: step, stepCompleted, employeeId }}
        />
      </StepCheckboxAndTextContainer>
      <MenuContainer ref={popoverWrapperRef}>
        {!step?.isLocked && (
          <MenuButton
            id={`cp-item-${step?.carrotPlanStepId}-menu-button`}
            aria-haspopup="true"
            aria-controls={showNotRelevant ? `cp-item-${step?.carrotPlanStepId}-menu` : null}
            aria-label={intl.formatMessage({ defaultMessage: "Carrot Plan item menu" })}
            onClick={toggleShowNotRelevant}
          >
            <SinglePathSVG icon="threeDots" fill={showNotRelevant ? "#e48400" : "white"} />
          </MenuButton>
        )}
        {showNotRelevant && (
          <StepMenu
            id={`cp-item-${step?.carrotPlanStepId}-menu`}
            role="menu"
            toggleShowNotRelevant={toggleShowNotRelevant}
            aria-labelledby={`cp-item-${step?.carrotPlanStepId}-menu-button`}
            markedNotRelevant={markedNotRelevant}
            handleNotRelevant={(stepId) => handleNotRelevant(stepId)}
            stepId={step?.carrotPlanStepId}
          />
        )}
      </MenuContainer>
    </Box>
  )
}

const PopoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  transform: translate(-85.5%);
  margin-top: 18px;
  z-index: 1;
  filter: drop-shadow(0px 9px 28px rgba(0, 0, 0, 0.25));
  > svg {
    align-self: flex-end;
    margin-right: 14px;
  }
  > div {
    display: flex;
    align-items: center;
    flex-direction: row;
    width: 262px;
    padding: 16px;
    align-self: flex-end;
  }
`

type StepMenuProps = {
  markedNotRelevant: boolean
  toggleShowNotRelevant: () => unknown
  handleNotRelevant: (stepId: number) => void
  stepId: number
} & React.HTMLAttributes<HTMLDivElement>

const StepMenu = ({
  toggleShowNotRelevant,
  markedNotRelevant,
  handleNotRelevant,
  stepId,
  ...otherProps
}: StepMenuProps): JSX.Element => (
  <PopoverContainer>
    <SinglePathSVG icon="popoverArrow" fill="white" />
    <Box
      onBlur={toggleShowNotRelevant}
      display="flex"
      minWidth="300px"
      padding={(theme) => theme.spacing(theme.tokens.spacing.md)}
      borderRadius={(theme) => theme.tokens.borderRadius.md}
      bgcolor={(theme) => theme.palette.background.paper}
      sx={{ "li::before": { display: "none" } }}
      component="ul"
      {...otherProps}
    >
      <li role="presentation">
        <FormControlLabel
          role="menuitem"
          label={<FormattedMessage defaultMessage="This isn't relevant to me" />}
          control={<Checkbox checked={markedNotRelevant} onChange={() => handleNotRelevant(stepId)} />}
        />
      </li>
    </Box>
  </PopoverContainer>
)

// @ts-expect-error TS7006
function useOutsideAlerter(ref, callback): void {
  useEffect(() => {
    // @ts-expect-error TS7006
    function handleClickOutside(event): void {
      if (ref.current && !ref.current.contains(event.target)) {
        callback()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [ref])
}
